import { useCallback, useLayoutEffect, useRef, useState } from "react";
import "./auth.css";
import olympeakLogo from "../assets/olympeak_logo_new.png";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL || "http://localhost:8788" || "http://localhost:3000" 
    : "";

const next = typeof window !== "undefined" ? window.location.origin : "";
const googleHref = `${API_BASE}/api/auth/google?next=${encodeURIComponent(next)}`;

const authBg = "/images/olympeak_visual_1.png";

export default function AuthPage({ onSuccess }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "login";
    const params = new URLSearchParams(window.location.search);
    const m = params.get("mode");
    return m === "register" ? "register" : "login";
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  // login
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");

  // forgot password
  const [forgotEmail, setForgotEmail] = useState("");

  // register
  const [regEmail, setRegEmail] = useState("");
  const [regNick, setRegNick] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regPassConfirm, setRegPassConfirm] = useState("");

  const passwordsMismatch =
    regPass && regPassConfirm && regPass !== regPassConfirm;

  // --- animated body height ---
  const bodyRef = useRef(null);
  const loginRef = useRef(null);
  const registerRef = useRef(null);
  const forgotRef = useRef(null);

  // ✅ stable height recalculation (fixes exhaustive-deps)
  const recalcBodyHeight = useCallback(() => {
    const body = bodyRef.current;
    const login = loginRef.current;
    const register = registerRef.current;
    const forgot = forgotRef.current;
    if (!body || !login || !register || !forgot) return;

    const active =
      mode === "login" ? login : mode === "register" ? register : forgot;

    body.style.setProperty("--body-h", `${active.scrollHeight}px`);
  }, [mode]);

  useLayoutEffect(() => {
    const login = loginRef.current;
    const register = registerRef.current;
    const forgot = forgotRef.current;
    if (!login || !register || !forgot) return;

    recalcBodyHeight();

    const ro = new ResizeObserver(recalcBodyHeight);
    ro.observe(login);
    ro.observe(register);
    ro.observe(forgot);

    window.addEventListener("resize", recalcBodyHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcBodyHeight);
    };
  }, [recalcBodyHeight]);

  // ✅ recalc on content changes too (success/error/messages)
  useLayoutEffect(() => {
    recalcBodyHeight();
  }, [
    recalcBodyHeight,
    err,
    info,
    loading,
    forgotEmail,
    identifier,
    password,
    regEmail,
    regNick,
    regPass,
    regPassConfirm,
  ]);

  function goLogin() {
    setErr("");
    setInfo("");
    setMode("login");
  }

  function goRegister() {
    setErr("");
    setInfo("");
    setMode("register");
  }

  function goForgot() {
    setErr("");
    setInfo("");

    // If user typed an email in the login identifier field, prefill it.
    const maybeEmail = String(identifier || "").trim();
    if (maybeEmail.includes("@")) setForgotEmail(maybeEmail);

    setMode("forgot");
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErr(data.error || "request_failed");
        return;
      }

      await onSuccess?.(data);
    } catch (error) {
      console.error("Login error:", error);
      setErr("request_failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setErr("");
    setInfo("");

    if (regPass !== regPassConfirm) {
      setErr("passwords_mismatch");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: regEmail,
          nickname: regNick,
          password: regPass,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErr(data.error || "request_failed");
        return;
      }

      await onSuccess?.(data);
    } catch (error) {
      console.error("Register error:", error);
      setErr("request_failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setErr("");
    setInfo("");

    const email = String(forgotEmail || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setErr("reset_email_required");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErr(data.error || "request_failed");
        return;
      }

      setInfo("reset_sent");
    } catch (error) {
      console.error("Password reset error:", error);
      setErr("request_failed");
    } finally {
      setLoading(false);
    }
  }

  const showTabs = mode === "login" || mode === "register";

  return (
    <div
      className="page-wrapper no-wallpaper auth-bg"
      style={{ backgroundImage: `url(${authBg})` }}
    >
      <div className="page-container auth-container">
        <div className="app-card auth-card glass">
          <div className="auth-brand">
            <a href="/" className="auth-logo-link">
              <img
                src={olympeakLogo}
                alt="OlymPeak"
                className="auth-brand-logo"
              />
            </a>
          </div>

          <div className="auth-form">
            {/* Tabs only for login/register */}
            {showTabs && (
              <div className="auth-tabs-wrap">
                <div className="tabs">
                  <div className="tabs-indicator" data-pos={mode} />
                  <button
                    type="button"
                    className={`tab ${mode === "login" ? "active" : ""}`}
                    onClick={goLogin}
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    className={`tab ${mode === "register" ? "active" : ""}`}
                    onClick={goRegister}
                  >
                    Register
                  </button>
                </div>
              </div>
            )}

            {/* Only this area animates height */}
            <div className="card-body" ref={bodyRef}>
              <div className="form-stack">
                {/* LOGIN */}
                <form
                  ref={loginRef}
                  onSubmit={handleLogin}
                  className={`form panel ${mode === "login" ? "show" : "hide"}`}
                  aria-hidden={mode !== "login"}
                >
                  <label className="app-label">Email or nickname</label>
                  <input
                    className="app-input"
                    placeholder="email@domain.com or nickname"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                  />

                  <label className="app-label">Password</label>
                  <input
                    className="app-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />

                  {mode === "login" && err && (
                    <div className="app-error">{prettyErr(err)}</div>
                  )}

                  {mode === "login" && info && (
                    <div className="app-success">{prettyInfo(info)}</div>
                  )}

                  <button className="btn primary" disabled={loading}>
                    {loading ? "Signing in…" : "Continue"}
                  </button>

                  <div className="or">or</div>

                  <a className="btn google" href={googleHref}>
                    Continue with Google
                  </a>

                  <div className="hint hint-small">
                    <button
                      type="button"
                      className="app-link link-button"
                      onClick={goForgot}
                      disabled={loading}
                    >
                      Forgot password?
                    </button>
                  </div>
                </form>

                {/* REGISTER */}
                <form
                  ref={registerRef}
                  onSubmit={handleRegister}
                  className={`form panel ${mode === "register" ? "show" : "hide"}`}
                  aria-hidden={mode !== "register"}
                >
                  <label className="app-label">Email</label>
                  <input
                    className="app-input"
                    type="email"
                    placeholder="you@example.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    autoComplete="email"
                  />

                  <label className="app-label">
                    Nickname <span className="muted">(optional)</span>
                  </label>
                  <input
                    className="app-input"
                    placeholder="e.g., Filio"
                    value={regNick}
                    onChange={(e) => setRegNick(e.target.value)}
                    autoComplete="nickname"
                  />

                  <label className="app-label">Password</label>
                  <input
                    className="app-input"
                    type="password"
                    placeholder="At least 8 characters"
                    value={regPass}
                    onChange={(e) => setRegPass(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                  />

                  <label className="app-label">Repeat password</label>
                  <input
                    className="app-input"
                    type="password"
                    placeholder="Repeat your password"
                    value={regPassConfirm}
                    onChange={(e) => setRegPassConfirm(e.target.value)}
                    autoComplete="new-password"
                  />

                  {passwordsMismatch && (
                    <div className="app-error">
                      {prettyErr("passwords_mismatch")}
                    </div>
                  )}

                  {mode === "register" && err && (
                    <div className="app-error">{prettyErr(err)}</div>
                  )}

                  <button
                    className="btn primary"
                    disabled={loading || passwordsMismatch}
                  >
                    {loading ? "Creating account…" : "Create account"}
                  </button>

                  <div className="or">or</div>

                  <a className="btn google" href={googleHref}>
                    Sign up with Google
                  </a>

                  <div className="hint hint-small">
                    By continuing you agree to our{" "}
                    <a
                      className="app-link"
                      href="/terms"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms
                    </a>{" "}
                    and{" "}
                    <a
                      className="app-link"
                      href="/privacy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy
                    </a>
                    .
                  </div>
                </form>

                {/* FORGOT PASSWORD */}
                <form
                  ref={forgotRef}
                  onSubmit={handleForgotPassword}
                  className={`form panel ${mode === "forgot" ? "show" : "hide"}`}
                  aria-hidden={mode !== "forgot"}
                >
                  <label className="app-label">Email</label>
                  <input
                    className="app-input"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    autoComplete="email"
                  />

                  {mode === "forgot" && err && (
                    <div className="app-error">{prettyErr(err)}</div>
                  )}

                  <button className="btn primary" disabled={loading}>
                    {loading ? "Sending…" : "Send reset email"}
                  </button>

                  <div className="hint hint-small">
                    <button
                      type="button"
                      className="app-link link-button"
                      onClick={goLogin}
                      disabled={loading}
                    >
                      Back to login
                    </button>
                  </div>

                  {/* ✅ success message BELOW (but styled like hint so it stays compact) */}
                  {mode === "forgot" && info && (
                    <div className="hint hint-small" style={{ marginTop: 6 }}>
                      <div className="app-success">{prettyInfo(info)}</div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function prettyErr(code) {
  const map = {
    invalid_credentials: "Invalid email/nickname or password.",
    email_in_use: "This email is already registered.",
    nickname_in_use: "This nickname is already taken. Try another one.",
    invalid_input: "Please provide a valid email and password (min 8 chars).",
    passwords_mismatch: "Passwords do not match.",
    request_failed: "Request failed. Try again.",
    missing_fields: "Please fill all required fields.",
    internal_error: "Something went wrong. Please try again.",
    reset_email_required: "Please enter a valid email address.",
  };
  return map[code] || code;
}

function prettyInfo(code) {
  const map = {
    reset_sent:
      "If an account exists for this email, you’ll receive a password reset message shortly.",
  };
  return map[code] || code;
}